import React, { useState, useRef, useEffect } from "react";
import logo from '../assets/logo-full.webp';
import { FaArrowRight, FaChevronDown } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import SearchProjects from "../pages/search";
import "../pages/marketing_search/styles/main.css";
import { MENU_FEATURES, FEATURED_FEATURE, MENU_SOLUTIONS } from "../pages/solutions/solutions-data";
import { useProductFlow, PRODUCT_MANAGEMENT } from "../utils/product-flow";
import { IS_DUAL_MODE } from "../utils/site-mode";

const PRODUCTS = [
    { label: "Project Management", path: "/products/project-management" },
    { label: "Published", path: "/" },
];

const HeaderAny = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { product } = useProductFlow();
    const isManagement = product === PRODUCT_MANAGEMENT;
    const [productsOpen, setProductsOpen] = useState(false);
    const [featuresOpen, setFeaturesOpen] = useState(false);
    const [solutionsOpen, setSolutionsOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const dropdownRef = useRef(null);
    const featuresRef = useRef(null);
    const solutionsRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setProductsOpen(false);
            }
            if (featuresRef.current && !featuresRef.current.contains(e.target)) {
                setFeaturesOpen(false);
            }
            if (solutionsRef.current && !solutionsRef.current.contains(e.target)) {
                setSolutionsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const goToSolution = (slug) => {
        setSolutionsOpen(false);
        navigate(`/solutions/${slug}`);
    };

    useEffect(() => {
        document.body.style.overflow = searchOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [searchOpen]);

    useEffect(() => {
        if (!searchOpen) return;
        const handler = (e) => { if (e.key === "Escape") setSearchOpen(false); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [searchOpen]);

    useEffect(() => {
        if (location.state?.scrollTo) {
            const section = document.getElementById(location.state.scrollTo);
            if (section) section.scrollIntoView({ behavior: "smooth" });
        }
    }, [location]);

    const handleNavigation = (section) => {
        setProductsOpen(false);
        const path = section.toLowerCase();
        if (path === "pricing") { navigate("/pricing"); return; }
        if (path === "about") { navigate("/about"); return; }
        if (path === "contact") { navigate("/contact-us"); return; }
        if (path === "features") { navigate("/platform/features"); return; }
        if (path === "search") { setSearchOpen(true); return; }

        if (location.pathname !== "/") {
            navigate("/", { state: { scrollTo: path } });
        } else {
            document.getElementById(path)?.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <>
            <nav>
                <div className="navbar-content">
                    <div className="left-section">
                        <button className="unoutlined-button" onClick={() => navigate("/")}>
                            <img src={logo} alt="ConRa" className="icon-full" />
                        </button>
                    </div>

                    <ul>
                        <li>
                            <button className="nav-link-black" onClick={() => navigate("/")}>
                                Home
                            </button>
                        </li>

                        {/* Products dropdown — only when both products are built in */}
                        {IS_DUAL_MODE && (
                            <li ref={dropdownRef} className="nav-dropdown-wrapper">
                                <button
                                    className="nav-link-black nav-dropdown-trigger"
                                    onClick={() => setProductsOpen(!productsOpen)}
                                >
                                    Products <FaChevronDown className={`nav-chevron ${productsOpen ? "open" : ""}`} />
                                </button>
                                {productsOpen && (
                                    <div className="nav-dropdown-menu">
                                        {PRODUCTS.map((p) => (
                                            <button
                                                key={p.path}
                                                className={`nav-dropdown-item ${location.pathname === p.path ? "active" : ""}`}
                                                onClick={() => { navigate(p.path); setProductsOpen(false); }}
                                            >
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </li>
                        )}

                        {/* Management flow: Features link */}
                        {isManagement && (
                            <li>
                                <button className="nav-link-black" onClick={() => handleNavigation("Features")}>
                                    Features
                                </button>
                            </li>
                        )}

                        {/* Published flow: Features mega-menu */}
                        {!isManagement && (
                            <li ref={featuresRef} className="nav-dropdown-wrapper">
                                <button
                                    className={`nav-link-black nav-dropdown-trigger ${featuresOpen ? "is-active" : ""}`}
                                    onClick={() => { setFeaturesOpen(!featuresOpen); setSolutionsOpen(false); }}
                                >
                                    Features <FaChevronDown className={`nav-chevron ${featuresOpen ? "open" : ""}`} />
                                </button>
                                {featuresOpen && (
                                    <div className="nav-mega-menu">
                                        <p className="nav-mega-label">Features</p>
                                        <div className="nav-mega-grid">
                                            {MENU_FEATURES.map((s) => (
                                                <button
                                                    key={s.slug}
                                                    className={`nav-mega-item ${location.pathname === `/solutions/${s.slug}` ? "active" : ""}`}
                                                    onClick={() => goToSolution(s.slug)}
                                                >
                                                    <span className="nav-mega-icon">
                                                        {s.menuIconSrc
                                                            ? <img src={s.menuIconSrc} alt="" className="nav-mega-icon-img" />
                                                            : <s.menuIcon />}
                                                    </span>
                                                    <span className="nav-mega-item-body">
                                                        <span className="nav-mega-item-title">{s.menuLabel}</span>
                                                        <span className="nav-mega-item-desc">{s.menuDesc}</span>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                        {/* {FEATURED_FEATURE && (
                                            <>
                                                <p className="nav-mega-featured-label">Check our new features</p>
                                                <button
                                                    className="nav-mega-featured"
                                                    onClick={() => goToSolution(FEATURED_FEATURE.slug)}
                                                >
                                                    <span className="nav-mega-icon nav-mega-icon-featured">
                                                        {FEATURED_FEATURE.menuIconSrc
                                                            ? <img src={FEATURED_FEATURE.menuIconSrc} alt="" className="nav-mega-icon-img" />
                                                            : <FEATURED_FEATURE.menuIcon />}
                                                    </span>
                                                    <span className="nav-mega-item-body">
                                                        <span className="nav-mega-featured-title">
                                                            {FEATURED_FEATURE.menuLabel} <FaArrowRight />
                                                        </span>
                                                        <span className="nav-mega-item-desc">{FEATURED_FEATURE.menuDesc}</span>
                                                    </span>
                                                </button>
                                            </>
                                        )} */}
                                    </div>
                                )}
                            </li>
                        )}

                        {/* Published flow: Solutions dropdown */}
                        {!isManagement && (
                            <li ref={solutionsRef} className="nav-dropdown-wrapper">
                                <button
                                    className={`nav-link-black nav-dropdown-trigger ${solutionsOpen ? "is-active" : ""}`}
                                    onClick={() => { setSolutionsOpen(!solutionsOpen); setFeaturesOpen(false); }}
                                >
                                    Solutions <FaChevronDown className={`nav-chevron ${solutionsOpen ? "open" : ""}`} />
                                </button>
                                {solutionsOpen && (
                                    <div className="nav-sol-menu">
                                        <div className="nav-sol-col">
                                            <p className="nav-sol-head">Institutional verification(MVI)</p>
                                            <button className={`nav-sol-item ${location.pathname === "/solutions/banks" ? "active" : ""}`} onClick={() => goToSolution("banks")}>
                                                <strong className="nav-sol-item-title">Disbursement proof</strong>
                                                <span className="nav-sol-item-desc">Financial institutions</span>
                                            </button>
                                            <button className={`nav-sol-item ${location.pathname === "/solutions/ngos" ? "active" : ""}`} onClick={() => goToSolution("ngos")}>
                                                <strong className="nav-sol-item-title">Delivery proof</strong>
                                                <span className="nav-sol-item-desc">NGOs</span>
                                            </button>
                                            <button className={`nav-sol-item ${location.pathname === "/solutions/green-building" ? "active" : ""}`} onClick={() => goToSolution("green-building")}>
                                                <strong className="nav-sol-item-title">Certification proof</strong>
                                                <span className="nav-sol-item-desc">Green Building</span>
                                            </button>
                                        </div>
                                        <div className="nav-sol-col">
                                            <p className="nav-sol-head">For your own project</p>
                                            <button className={`nav-sol-item ${location.pathname === "/solutions/diaspora" ? "active" : ""}`} onClick={() => goToSolution("diaspora")}>
                                                <strong className="nav-sol-item-title">Remote proof</strong>
                                                <span className="nav-sol-item-desc">Diaspora &amp; owners</span>
                                            </button>
                                        </div>
                                        <div className="nav-sol-col">
                                            <p className="nav-sol-head">Professional track record</p>
                                            <button className={`nav-sol-item ${location.pathname === "/solutions/contractors" ? "active" : ""}`} onClick={() => goToSolution("contractors")}>
                                                <strong className="nav-sol-item-title">Verified work history</strong>
                                                <span className="nav-sol-item-desc">Contractors</span>
                                            </button>
                                            <button className={`nav-sol-item ${location.pathname === "/solutions/borderless" ? "active" : ""}`} onClick={() => goToSolution("borderless")}>
                                                <strong className="nav-sol-item-title">Portable profile</strong>
                                                <span className="nav-sol-item-desc">Borderless A&amp;E</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        )}

                        {/* {["Pricing", "About", "Search"].map((section) => ( */}
                        {["Pricing", "Search"].map((section) => (
                            <li key={section}>
                                <button className="nav-link-black" onClick={() => handleNavigation(section)}>
                                    {section}
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="right-section">
                        <button
                            className="filled-button"
                            type="button"
                            onClick={() => {
                                if (location.pathname === "/solutions/borderless") {
                                    document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
                                } else {
                                    window.open("https://web.conra.net", "_blank");
                                }
                            }}
                        >
                            Get Started <FaArrowRight style={{ marginLeft: "6px" }} />
                        </button>
                    </div>
                </div>
            </nav>

            <div
                className={`search-drawer-overlay ${searchOpen ? "is-open" : ""}`}
                onClick={() => setSearchOpen(false)}
                aria-hidden={!searchOpen}
            >
                <aside
                    className={`search-drawer-panel ${searchOpen ? "is-open" : ""}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Search projects"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="search-drawer-header">
                        <img src={logo} alt="ConRa" style={{ height: "32px", width: "auto", margin: 0 }} />
                        <button
                            type="button"
                            className="search-drawer-close"
                            onClick={() => setSearchOpen(false)}
                            aria-label="Close search"
                            style={{ marginLeft: "auto" }}
                        >
                            ×
                        </button>
                    </div>
                    <div className="search-drawer-content">
                        <SearchProjects onClose={() => setSearchOpen(false)} />
                    </div>
                </aside>
            </div>
        </>
    );
};

export default HeaderAny;
